import React, { useEffect, useRef, useState } from "react";
import { css, styled } from "../../../theme/styled";
import { Input } from "../TextField/TextField";

const OTP_DIGIT_INPUT_WIDTH_PX = 52;
const OTP_DIGIT_INPUT_GAP_PX = 8;
const OTP_DIGIT_INPUT_SMALL_WIDTH_PX = 40;
const OTP_DIGIT_INPUT_SMALL_GAP_PX = 4;

interface Props {
  /** How many OTP digit inputs to show, i.e. how long should the OTP code be. */
  digits: number;
  /** Called whenever the user change the OTP input, used for retrieving the input state. */
  onOtpUpdate: (value: string) => void;
  /** Used to indicate error */
  isError?: boolean;
  /** Used to disable input */
  isDisabled?: boolean;
}

export const OtpInput = (props: Props): React.JSX.Element => {
  const [otpInput, setOtpInput] = useState<string[]>(
    new Array(props.digits).fill(""),
  );

  // We need to keep track of all individual OTP digit inputs so that move focus between them.
  const otpDigitInputRefs = useRef<HTMLInputElement[]>([]);

  function moveFocusTo(to: number): void {
    if (to < 0 || to > otpInput.length) {
      // Don't do anything if we're out of boundary
      return;
    }

    otpDigitInputRefs.current[to]?.focus();
  }

  function onChange({
    event,
    index,
  }: {
    event: React.ChangeEvent<HTMLInputElement>;
    index: number;
  }): void {
    const changedOtpDigitValue = event.target.value;

    // We need to check what type of value we got in the OTP digit input since we can receive
    // digits, letters or longer strings (when copy pasting).
    const valueType = validateOtpDigitInput({
      digitValueToValidate: changedOtpDigitValue,
      fullOtpLength: otpInput.length,
    });

    switch (valueType) {
      case "digit_replacement": {
        // We received two digits in the input (one that was there before and another that
        // the user wants to replace with). Let's say the input had a '6' from before and the user
        // presses '7', then we get either '67' or '76' as the input value here (depending on the cursor placement).
        // But none the less we want to replace the '6' with a '7'. So we need to take care of both cases.
        const oldValue = otpInput[index];

        if (oldValue) {
          const newValue = changedOtpDigitValue.replace(oldValue, "");

          setOtpInput((current) => {
            const updated = [...current];
            updated[index] = newValue;
            return updated;
          });

          // Go to the next OTP box
          moveFocusTo(index + 1);
        }
        break;
      }
      case "full_otp": {
        // Since we got a full OTP string we need to split it so we can
        // replace the array that stores individual OTP digits.
        const updated = changedOtpDigitValue.split("");
        setOtpInput(updated);

        // Move the focus to the last digit.
        moveFocusTo(otpInput.length - 1);
        break;
      }
      case "digit": {
        // Only update specific OTP digit
        setOtpInput((current) => {
          const updated = [...current];
          updated[index] = changedOtpDigitValue;
          return updated;
        });

        // Go to the next OTP box
        moveFocusTo(index + 1);
        break;
      }
      case "invalid":
      default:
        break;
    }
  }

  function onKeyDown({
    event,
    index,
  }: {
    event: React.KeyboardEvent<HTMLInputElement>;
    index: number;
  }): void {
    switch (event.key) {
      case "ArrowLeft": {
        // Go to the previous OTP box
        moveFocusTo(index - 1);
        break;
      }
      case "ArrowRight": {
        // Go to the next OTP box
        moveFocusTo(index + 1);
        break;
      }
      case "Backspace": {
        if (event.currentTarget.value === "") {
          // If we don't have a value in the current box, move focus to previous OTP box and also remove its value.
          setOtpInput((current) => {
            const updated = [...current];
            updated[index - 1] = "";
            return updated;
          });
          moveFocusTo(index - 1);
        } else {
          // Clear value in the current box
          setOtpInput((current) => {
            const updated = [...current];
            updated[index] = "";
            return updated;
          });
        }
        break;
      }
    }
  }

  // Send signals back to the parent component whenever input value changes.
  useEffect(() => props.onOtpUpdate(otpInput.join("")), [otpInput]);

  return (
    <OtpWrapper data-testid="otp">
      {otpInput.map((digit, index) => (
        <div
          key={index}
          className={css({
            minWidth: OTP_DIGIT_INPUT_SMALL_WIDTH_PX,
            maxWidth: OTP_DIGIT_INPUT_WIDTH_PX,
            flexShrink: 1,
          })}
        >
          <StyledOtpInput
            ref={(ref) => {
              if (ref) {
                // Add input ref to list of all OTP inputs (every digit has its own input).
                otpDigitInputRefs.current[index] = ref;
              }
            }}
            key={index}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code" // Support for auto complete from SMS:es
            pattern="\d{1}"
            value={digit}
            maxLength={otpInput.length} // Needs accept max length of the whole OTP input length so we can paste in an OTP code
            inputSize="medium"
            onChange={(event) => onChange({ event: event, index: index })}
            severity={props.isError ? "error" : "info"}
            disabled={props.isDisabled}
            onKeyDown={(event) => onKeyDown({ event: event, index: index })}
          />
        </div>
      ))}
    </OtpWrapper>
  );
};

const OtpWrapper = styled.div({
  display: "flex",
  gap: `$spacing-${OTP_DIGIT_INPUT_GAP_PX}`,
  $small: {
    gap: `$spacing-${OTP_DIGIT_INPUT_SMALL_GAP_PX}`,
  },
});

const StyledOtpInput = styled(Input)({
  textAlign: "center",
});

/**
 * Responsible for checking what type of input was received for a given digit input.
 */
function validateOtpDigitInput({
  digitValueToValidate,
  fullOtpLength,
}: {
  digitValueToValidate: string;
  fullOtpLength: number;
}): "digit" | "full_otp" | "digit_replacement" | "invalid" {
  if (digitValueToValidate === "") {
    return "invalid";
  }

  if (Number.isNaN(parseInt(digitValueToValidate, 10))) {
    // Invalid if we get something that cannot be parsed as a number.
    return "invalid";
  }

  if (digitValueToValidate.length > fullOtpLength) {
    // Invalid if you more digits than the full OTP can be (for an example when copy pasting).
    return "invalid";
  }

  if (
    digitValueToValidate.length > 2 &&
    digitValueToValidate.length < fullOtpLength
  ) {
    // Invalid if you provide more than a two digits, but less than the whole OTP (for an example when copy pasting).
    return "invalid";
  }

  if (digitValueToValidate.length === 2) {
    // If we have two digits in the input we can assume that it is because of replacement.
    return "digit_replacement";
  }

  if (digitValueToValidate.length === fullOtpLength) {
    // If we get an input that is the same length as our full OTP code, then we can assume it's copy pasted OTP code.
    return "full_otp";
  }

  return "digit";
}
